/**
 * AI Chat Button 组件
 * 显示在特定页面的固定位置，带呼吸动画
 */

import { FC, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAIChatStore } from '@/store/useAIChatStore';
import { useUserStore } from '@/store/useUserStore';
import AIChatWelcomePopup from '@/components/AIChatWelcomePopup';
import AIChatModal from '@/components/AIChatModal';
import type { ContextType } from '@/types/aiChat';
import { trackEvent } from '@/utils/analytics';
import { EVENTS } from '@/utils/analyticsEvents';
import { buildCoachBaseProps, buildCoachCoreProps } from '@/utils/aiCoachAnalytics';
import styles from './index.module.scss';

const AIChatButton: FC = () => {
  const location = useLocation();
  const { t } = useTranslation();
  const [showButton, setShowButton] = useState(false);
  const [contextInfo, setContextInfo] = useState<{ type: ContextType; id: number } | null>(null);
  const [hasClicked, setHasClicked] = useState(false);
  const prevPathRef = useRef<string>(location.pathname);
  const viewedContextKeyRef = useRef<string>('');

  const { setCurrentContext, setModalOpen, isModalOpen, dismissWelcomePopup, currentContext, welcomePopupDismissed } = useAIChatStore();
  const { user } = useUserStore();

  // 判断当前页面是否应该显示 AI Chat 按钮
  useEffect(() => {
    if (prevPathRef.current !== location.pathname) {
      if (isModalOpen) {
        setModalOpen(false);
      }
      prevPathRef.current = location.pathname;
    }

    const path = location.pathname;
    let shouldShow = false;
    let type: ContextType | null = null;
    let id: number | null = null;

    // CV 分析详情页
    if (path.startsWith('/document-analysis/cv/')) {
      const match = path.match(/\/document-analysis\/cv\/(\d+)/);
      if (match && match[1]) {
        shouldShow = true;
        type = 'cv-analysis';
        id = parseInt(match[1], 10);
      }
    }
    // CL 分析详情页
    else if (path.startsWith('/document-analysis/cover-letter/')) {
      const match = path.match(/\/document-analysis\/cover-letter\/(\d+)/);
      if (match && match[1]) {
        shouldShow = true;
        type = 'cl-analysis';
        id = parseInt(match[1], 10);
      }
    }
    // 面试分析详情页
    else if (path.startsWith('/interview-simulation/analysis/')) {
      const match = path.match(/\/interview-simulation\/analysis\/([^/]+)/);
      if (match && match[1]) {
        shouldShow = true;
        type = 'interview';
        if (/^\d+$/.test(match[1])) {
          id = parseInt(match[1], 10);
        } else if (currentContext?.type === 'interview') {
          id = currentContext.id;
        }
      }
    }

    setShowButton(shouldShow && id !== null);
    if (shouldShow && type && id !== null) {
      setContextInfo({ type, id });
      setCurrentContext(type, id);
      const contextKey = `${type}-${id}`;
      if (viewedContextKeyRef.current !== contextKey) {
        trackEvent(EVENTS.AI_COACH_BUTTON_VIEWED, buildCoachBaseProps({ type, id }, user?.subscription_plan));
        viewedContextKeyRef.current = contextKey;
      }
      // 每次进入新页面，重置点击状态，重新显示呼吸动画
      setHasClicked(false);
    } else {
      setContextInfo(null);
      viewedContextKeyRef.current = '';
    }
  }, [location.pathname, setCurrentContext, setModalOpen, isModalOpen, currentContext?.id, currentContext?.type, user?.subscription_plan]);

  // 点击按钮
  const handleClick = () => {
    if (contextInfo) {
      const contextKey = `${contextInfo.type}-${contextInfo.id}`;
      const popupWasShown = !welcomePopupDismissed[contextKey];
      const baseProps = buildCoachBaseProps(contextInfo, user?.subscription_plan);
      const coreProps = buildCoachCoreProps(contextInfo);
      if (popupWasShown) {
        trackEvent(EVENTS.AI_COACH_POPUP_DISMISSED, {
          ...coreProps,
          dismiss_method: 'click_chat',
        });
      }
      trackEvent(EVENTS.AI_COACH_OPENED, {
        ...baseProps,
        popup_was_shown: popupWasShown,
      });
      dismissWelcomePopup(contextKey);
    }
    setHasClicked(true);
    setModalOpen(true);
  };

  if (!showButton || !contextInfo) {
    return null;
  }

  return (
    <>
      <div className={styles.aiChatButtonWrapper}>
        {/* Welcome Popup 包裹按钮 */}
        <AIChatWelcomePopup contextInfo={contextInfo}>
          <button className={`${styles.aiChatButton} ${hasClicked ? styles.clicked : ''}`} onClick={handleClick} aria-label={t('aiChat.buttonAriaLabel')}>
            {/* SVG 渐变定义 */}
            <svg width="0" height="0" style={{ position: 'absolute' }}>
              <defs>
                <linearGradient id="aiChatIconGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#ff6b9d" />
                  <stop offset="100%" stopColor="#ff4757" />
                </linearGradient>
              </defs>
            </svg>

            <svg className={styles.icon} aria-hidden="true">
              <use xlinkHref="#icon-a-AIchat" fill="url(#aiChatIconGradient)"></use>
            </svg>
          </button>
        </AIChatWelcomePopup>
      </div>

      {/* AI Chat Modal */}
      {isModalOpen && <AIChatModal />}
    </>
  );
};

export default AIChatButton;
